import React from 'react';
import { Modal } from '../Modal';
import { CompanyInfoMetrics, CompanyInfoResponse } from '../../../hooks/useCompanyInfo';
import { AvatarSprite } from '../../avatar/AvatarSprite';
import { cn } from '../../../utils/cn';

interface CompanyInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNext: () => void;
  /**
   * Prefetched company info. Must be non-null when isOpen is true — the
   * parent is responsible for suppressing the modal entirely when AI content
   * is not cached for today's stock (see FinishedModal).
   */
  data: CompanyInfoResponse | null;
}

const DISCLAIMER =
  'Data may be inaccurate or out of date. For educational purposes only. Not financial advice.';

interface MetricTile {
  label: string;
  sub: string;
  value: string | null;
}

function buildMetricTiles(metrics: CompanyInfoMetrics | undefined): MetricTile[] {
  return [
    { label: 'Market Cap', sub: 'Today', value: metrics?.marketCap ?? null },
    { label: 'P / E', sub: 'Trailing', value: metrics?.trailingPE ?? null },
    { label: 'Revenue', sub: 'TTM', value: metrics?.annualRevenue ?? null },
    { label: 'Revenue Growth', sub: 'YoY', value: metrics?.quarterlyRevenueGrowth ?? null },
    { label: 'P / S', sub: 'TTM', value: metrics?.priceToSales ?? null },
    { label: 'Debt / Equity', sub: 'MRQ', value: metrics?.debtToEquity ?? null },
    { label: 'Net Margin', sub: 'Last reported', value: metrics?.profitMargin ?? null },
    { label: 'Dividend Yield', sub: 'Forward', value: metrics?.fiveYearDividendYield ?? null },
  ];
}

const Eyebrow: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <span
    className={cn(
      'font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-[#8A8378] dark:text-muted-foreground',
      className,
    )}
  >
    {children}
  </span>
);

const BodyText: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <p className="m-0 text-[15px] leading-[1.55] text-[#5A5A5A] dark:text-muted-foreground">
    {children}
  </p>
);

const CaseCard: React.FC<{
  variant: 'bull' | 'bear';
  heading: string;
  items: string[];
}> = ({ variant, heading, items }) => {
  const isBull = variant === 'bull';
  return (
    <div
      className={cn(
        'rounded-[14px] border px-4 py-3.5',
        isBull
          ? 'border-[rgba(45,184,122,0.25)] bg-[rgba(45,184,122,0.12)] dark:border-emerald-500/30 dark:bg-emerald-500/10'
          : 'border-[rgba(216,92,92,0.25)] bg-[rgba(216,92,92,0.12)] dark:border-rose-500/30 dark:bg-rose-500/10',
      )}
    >
      <Eyebrow className={isBull ? 'text-[#2DB87A] dark:text-emerald-400' : 'text-[#D85C5C] dark:text-rose-400'}>
        {isBull ? 'Bull case' : 'Bear case'}
      </Eyebrow>
      <h4 className="mb-2 mt-1 font-serif text-[1.1rem] text-[#1A1A1A] dark:text-foreground">{heading}</h4>
      <ul className="m-0 grid list-none gap-2 p-0">
        {items.slice(0, 3).map((item, idx) => (
          <li
            key={idx}
            className="flex gap-2.5 text-[14.5px] leading-[1.5] text-[#1A1A1A] dark:text-foreground"
          >
            <span
              className={cn(
                'mt-2 h-1.5 w-1.5 shrink-0 rounded-full',
                isBull ? 'bg-[#2DB87A]' : 'bg-[#D85C5C]',
              )}
              aria-hidden="true"
            />
            <span dangerouslySetInnerHTML={{ __html: item }} />
          </li>
        ))}
      </ul>
    </div>
  );
};

const MetricTileView: React.FC<{ tile: MetricTile }> = ({ tile }) => (
  <div className="flex min-h-[84px] flex-col items-center gap-1 rounded-[10px] border border-[rgba(60,50,30,0.10)] bg-white/70 px-3.5 py-3 text-center dark:border-border dark:bg-muted/40">
    <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8A8378] dark:text-muted-foreground">
      {tile.label}
    </span>
    <span className="font-serif text-[1.35rem] leading-tight text-[#1A1A1A] dark:text-foreground">
      {tile.value ?? '—'}
    </span>
    <span className="mt-auto text-[11.5px] text-[#5A5A5A] dark:text-muted-foreground">
      {tile.sub}
    </span>
  </div>
);

export const CompanyInfoModal: React.FC<CompanyInfoModalProps> = ({
  isOpen,
  onClose,
  onNext,
  data,
}) => {
  // The parent gates rendering on data availability; if data is somehow
  // missing when isOpen flips true, render nothing rather than an empty shell.
  if (!data) return null;

  const aiContent = data.aiContent;
  const sector = data.sector ?? '';
  const ticker = data.symbol ?? '';
  const resolvedName = data.companyName;
  const resolvedLogo = data.logoUrl;

  const metricTiles = buildMetricTiles(data.metrics);
  const initial = resolvedName?.trim()?.charAt(0)?.toUpperCase() ?? 'L';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      animationStyle="slide-up"
      backdropBlur
      showCloseButton={false}
      className="max-w-[calc(100vw-2rem)] sm:max-w-[560px] max-h-[92dvh] sm:max-h-[88dvh]"
    >
      <div className="-m-4 sm:-m-6 flex flex-col">
        {/* Drag handle (mobile only) */}
        <div
          aria-hidden="true"
          className="mx-auto mt-2.5 mb-1 h-[5px] w-11 shrink-0 rounded-full bg-[rgba(60,50,30,0.18)] dark:bg-muted-foreground/40 sm:hidden"
        />

        {/* Tagline */}
        <p className="m-0 px-4 pt-2 pb-1 text-center font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-[#8A8378] dark:text-muted-foreground sm:px-[18px]">
          Learn about stocks daily by reading about the company behind them
        </p>

        {/* Sticky header */}
        <header className="sticky top-0 z-10 flex items-center gap-3.5 bg-[#FDF6EC] px-4 py-3.5 dark:bg-background sm:px-[18px]">
          <div
            className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-[14px] border border-[rgba(60,50,30,0.10)] bg-white/90 font-serif text-[22px] text-[#8A8378] shadow-[0_8px_32px_rgba(60,50,30,0.10)] dark:border-border dark:bg-muted/40 dark:text-muted-foreground"
            aria-hidden="true"
          >
            {resolvedLogo ? (
              <img
                src={resolvedLogo}
                alt=""
                className="h-full w-full object-contain"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              initial
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="m-0 truncate font-serif text-[clamp(1.4rem,5vw,1.7rem)] leading-[1.15] tracking-tight text-[#1A1A1A] dark:text-foreground">
              {resolvedName || 'Company'}
            </h2>
            <div className="mt-1 flex items-center gap-2 text-xs text-[#5A5A5A] dark:text-muted-foreground">
              <span className="font-mono font-semibold tracking-[0.04em]">{ticker}</span>
              {sector && (
                <>
                  <span
                    aria-hidden="true"
                    className="h-[3px] w-[3px] rounded-full bg-[#8A8378] dark:bg-muted-foreground"
                  />
                  <span className="rounded-full bg-[rgba(232,168,56,0.14)] px-2.5 py-[3px] text-[11px] font-semibold text-[#8A5E0F] dark:bg-amber-400/20 dark:text-amber-200">
                    {sector}
                  </span>
                </>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-[rgba(60,50,30,0.10)] bg-white/70 text-lg leading-none text-[#5A5A5A] transition-colors hover:bg-white dark:border-border dark:bg-muted/40 dark:text-muted-foreground dark:hover:bg-muted"
          >
            ×
          </button>
        </header>

        {/* Body */}
        <div className="flex flex-col gap-[18px] px-4 pb-6 pt-1.5 sm:px-[18px]">
          <section className="flex flex-col gap-2">
            <Eyebrow>Overview</Eyebrow>
            <BodyText>{aiContent.overview}</BodyText>
          </section>

          <section className="flex flex-col gap-2">
            <Eyebrow>Why it moved · last 1Y</Eyebrow>
            <BodyText>{aiContent.priceMovement}</BodyText>
          </section>

          <CaseCard
            variant="bull"
            heading="Reasons to be optimistic"
            items={aiContent.catalysts}
          />

          <CaseCard
            variant="bear"
            heading="Reasons to be cautious"
            items={aiContent.risks}
          />

          <section className="flex flex-col gap-2">
            <Eyebrow>Key financial metrics</Eyebrow>
            <div className="grid grid-cols-2 gap-2.5">
              {metricTiles.map((tile) => (
                <MetricTileView key={tile.label} tile={tile} />
              ))}
            </div>
          </section>

          {aiContent.baronAnalysis && aiContent.baronAnalysis.length === 3 && (
            <section className="flex flex-col gap-2">
              <Eyebrow>Baron Wuffett analysis</Eyebrow>
              <div className="rounded-[14px] border border-[rgba(232,168,56,0.25)] bg-[rgba(232,168,56,0.08)] p-3.5 dark:border-amber-500/30 dark:bg-amber-500/10">
                <div className="flex items-center gap-3 sm:items-start sm:gap-4">
                  <div className="shrink-0">
                    <AvatarSprite
                      gesture="baron-wuffett-ponder"
                      size={64}
                      className="sm:hidden"
                    />
                    <AvatarSprite
                      gesture="baron-wuffett-ponder"
                      size={96}
                      className="hidden sm:block"
                    />
                  </div>
                  <ul className="m-0 grid flex-1 list-none gap-2 p-0">
                    {aiContent.baronAnalysis.slice(0, 3).map((item, idx) => (
                      <li
                        key={idx}
                        className="flex gap-2.5 text-[14.5px] leading-[1.5] text-[#1A1A1A] dark:text-foreground"
                      >
                        <span
                          aria-hidden="true"
                          className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#E8A838] dark:bg-amber-400"
                        />
                        <span dangerouslySetInnerHTML={{ __html: item }} />
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="m-0 mt-3 text-[11px] italic leading-[1.4] text-[#8A8378] dark:text-muted-foreground">
                  General observations from an AI persona — not financial advice.
                </p>
              </div>
            </section>
          )}
        </div>

        {/* Sticky footer */}
        <footer
          className="sticky bottom-0 z-10 flex items-center justify-between gap-3 border-t border-[rgba(60,50,30,0.10)] bg-[#FDF6EC] px-4 py-2 dark:border-border dark:bg-background"
          style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}
        >
          <p className="m-0 flex-1 text-[10px] leading-[1.35] text-[#8A8378] dark:text-muted-foreground">
            {DISCLAIMER}
          </p>
          <button
            type="button"
            onClick={onNext}
            className="inline-flex min-h-9 shrink-0 items-center gap-1.5 rounded-[10px] border border-[#1A1A1A] bg-[#1A1A1A] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-black active:translate-y-[1px] dark:border-foreground dark:bg-foreground dark:text-background dark:hover:bg-foreground/90"
          >
            Next
            <span className="font-mono font-semibold" aria-hidden="true">→</span>
          </button>
        </footer>
      </div>
    </Modal>
  );
};

export default CompanyInfoModal;
